import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Loader2, LogIn, UserPlus, KeyRound, FlaskConical, MailCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Field } from '@/components/forms/CompanyFields';
import { FieldError } from '@/components/common/Primitives';
import { authService } from '@/services/api';
import { useSeo } from '@/lib/seo';

export const DemoAuthNotice = () => (
  <div className="flex items-start gap-2 rounded-md border border-white/10 bg-white/[0.03] px-3 py-2.5 text-xs text-muted-foreground" data-testid="auth-demo-notice">
    <FlaskConical className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
    <span>Demonstração: isto não é uma autenticação real. A conta existe apenas neste navegador e pode ser apagada a qualquer momento. Não use uma senha que você utiliza em outros serviços.</span>
  </div>
);

export const AuthShell = ({ title, subtitle, children, footer }) => (
  <div className="relative flex min-h-[calc(100vh-8rem)] items-center justify-center overflow-hidden px-4 py-12">
    <div className="pointer-events-none absolute inset-0 hero-glow" />
    <div className="pointer-events-none absolute inset-0 grid-floor opacity-30 [mask-image:radial-gradient(ellipse_at_center,black,transparent_70%)]" />
    <div className="surface relative w-full max-w-md p-6 sm:p-8">
      <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
      {subtitle && <p className="mt-1.5 text-sm text-muted-foreground">{subtitle}</p>}
      <div className="mt-6">{children}</div>
      {footer && <div className="mt-6 border-t border-white/10 pt-5 text-center text-sm text-muted-foreground">{footer}</div>}
    </div>
  </div>
);

export const PasswordInput = ({ id, value, onChange, invalid, testId, autoComplete = 'current-password' }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input id={id} type={show ? 'text' : 'password'} value={value} onChange={onChange} autoComplete={autoComplete} aria-invalid={invalid} className="pr-10" data-testid={testId} />
      <button type="button" onClick={() => setShow((s) => !s)} aria-label={show ? 'Ocultar senha' : 'Mostrar senha'} className="absolute right-2 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded text-muted-foreground hover:text-foreground">
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
};

export const validateAccount = (f, { requireName = true } = {}) => {
  const e = {};
  if (requireName && f.name.trim().length < 3) e.name = 'Informe o nome do responsável.';
  if (!/^\S+@\S+\.\S+$/.test(f.email)) e.email = 'Informe um e-mail válido.';
  if (f.password.length < 8) e.password = 'A senha precisa ter ao menos 8 caracteres.';
  else if (!/[A-Za-z]/.test(f.password) || !/\d/.test(f.password)) e.password = 'Use letras e números.';
  if (f.confirm !== undefined && f.confirm !== f.password) e.confirm = 'As senhas não conferem.';
  return e;
};

// Botão de conta de exemplo desativado: não é mais exibido em nenhuma tela de entrada.
export const SampleLoginButton = () => null;

export function Login() {
  useSeo({ title: 'Entrar' });
  const navigate = useNavigate();
  const location = useLocation();
  const [f, setF] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (!/^\S+@\S+\.\S+$/.test(f.email)) er.email = 'Informe um e-mail válido.';
    if (!f.password) er.password = 'Informe a senha.';
    setErrors(er);
    setErr('');
    if (Object.keys(er).length) return;
    setBusy(true);
    try {
      await authService.login(f);
      toast.success('Bem-vindo de volta');
      navigate(location.state?.from || '/painel');
    } catch (ex) {
      setErr(ex.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell title="Entrar" subtitle="Acesse o painel da sua empresa." footer={<>Ainda não tem conta? <Link to="/criar-conta" className="text-primary hover:underline" data-testid="login-to-signup-link">Criar conta</Link></>}>
      <form onSubmit={submit} noValidate className="space-y-4" data-testid="login-form">
        <Field id="login-email-input" label="E-mail" error={errors.email}>
          <Input id="login-email-input" type="email" autoComplete="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} aria-invalid={!!errors.email} data-testid="login-email-input" />
        </Field>
        <Field id="login-password-input" label="Senha" error={errors.password}>
          <PasswordInput id="login-password-input" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} invalid={!!errors.password} testId="login-password-input" />
        </Field>
        <div className="flex justify-end"><Link to="/recuperar-senha" className="text-xs text-muted-foreground hover:text-primary" data-testid="login-forgot-link">Esqueci minha senha</Link></div>
        {err && <div role="alert" className="rounded-md border border-[rgba(255,59,92,0.3)] bg-[rgba(255,59,92,0.08)] px-3 py-2 text-sm text-[rgb(255,150,165)]" data-testid="login-error">{err}</div>}
        <Button type="submit" className="btn-glow w-full" disabled={busy} data-testid="login-form-submit-button">{busy ? <Loader2 className="animate-spin" /> : <LogIn />} Entrar</Button>
      </form>
    </AuthShell>
  );
}

export function Signup() {
  useSeo({ title: 'Criar conta' });
  const navigate = useNavigate();
  const [f, setF] = useState({ name: '', email: '', password: '', confirm: '' });
  const [terms, setTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const er = validateAccount(f);
    if (!terms) er.terms = 'É necessário aceitar os termos de uso e a política de privacidade.';
    setErrors(er);
    if (Object.keys(er).length) return;
    setBusy(true);
    try {
      await authService.signup(f);
      toast.success('Conta criada', { description: 'Agora cadastre sua empresa.' });
      navigate('/cadastrar-empresa');
    } catch (ex) {
      setErrors({ email: ex.message });
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthShell title="Criar conta" subtitle="Conta do responsável pela empresa." footer={<>Já tem conta? <Link to="/entrar" className="text-primary hover:underline">Entrar</Link></>}>
      <form onSubmit={submit} noValidate className="space-y-4" data-testid="signup-form">
        <Field id="signup-name-input" label="Nome do responsável" required error={errors.name}><Input id="signup-name-input" autoComplete="name" value={f.name} onChange={set('name')} data-testid="signup-name-input" /></Field>
        <Field id="signup-email-input" label="E-mail" required error={errors.email}><Input id="signup-email-input" type="email" autoComplete="email" value={f.email} onChange={set('email')} data-testid="signup-email-input" /></Field>
        <Field id="signup-password-input" label="Senha" required error={errors.password} hint="Mínimo de 8 caracteres, com letras e números."><PasswordInput id="signup-password-input" autoComplete="new-password" value={f.password} onChange={set('password')} testId="signup-password-input" /></Field>
        <Field id="signup-confirm-input" label="Confirmar senha" required error={errors.confirm}><PasswordInput id="signup-confirm-input" autoComplete="new-password" value={f.confirm} onChange={set('confirm')} testId="signup-confirm-input" /></Field>
        <TermsCheckbox checked={terms} onChange={setTerms} error={errors.terms} id="signup-terms" />
        <Button type="submit" className="btn-glow w-full" disabled={busy} data-testid="signup-submit-button">{busy ? <Loader2 className="animate-spin" /> : <UserPlus />} Criar conta</Button>
      </form>
    </AuthShell>
  );
}

export const TermsCheckbox = ({ checked, onChange, error, id }) => (
  <div>
    <div className="flex items-start gap-2.5">
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(!!v)} className="mt-0.5" aria-invalid={!!error} data-testid={`${id}-checkbox`} />
      <Label htmlFor={id} className="text-sm font-normal leading-snug text-muted-foreground">
        Li e aceito os <Link to="/termos" target="_blank" className="text-primary hover:underline">termos de uso</Link>, a <Link to="/privacidade" target="_blank" className="text-primary hover:underline">política de privacidade</Link> e as <Link to="/diretrizes" target="_blank" className="text-primary hover:underline">diretrizes da comunidade</Link>.
      </Label>
    </div>
    <FieldError msg={error} />
  </div>
);

export function Recover() {
  useSeo({ title: 'Recuperar senha' });
  const [email, setEmail] = useState('');
  const [err, setErr] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) return setErr('Informe um e-mail válido.');
    setErr('');
    setBusy(true);
    try {
      await authService.recover(email);
    } finally {
      setBusy(false);
      setSent(true);
    }
    return null;
  };
  return (
    <AuthShell title="Recuperar senha" subtitle="Informe o e-mail da conta." footer={<Link to="/entrar" className="text-primary hover:underline">Voltar para entrar</Link>}>
      {sent ? (
        <div className="space-y-4 text-center" data-testid="recover-success">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-xl border border-white/10 bg-white/5"><MailCheck className="h-5 w-5 text-primary" /></div>
          <p className="text-sm text-muted-foreground">Se houver uma conta para <span className="text-foreground">{email}</span>, você vai receber as instruções por e-mail.</p>
        </div>
      ) : (
        <form onSubmit={submit} noValidate className="space-y-4" data-testid="recover-form">
          <Field id="recover-email-input" label="E-mail" error={err}><Input id="recover-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="recover-email-input" /></Field>
          <Button type="submit" className="btn-glow w-full" disabled={busy} data-testid="recover-submit-button">{busy ? <Loader2 className="animate-spin" /> : <KeyRound />} Enviar instruções</Button>
        </form>
      )}
    </AuthShell>
  );
}